import { Link } from 'react-router-dom';
import {
  Title1,
  Title3,
  Body1,
  Card,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  BookTemplateRegular,
  ChartMultipleRegular,
  MoneyRegular,
  DataPieRegular,
  WrenchScrewdriverRegular,
  ShieldKeyholeRegular,
  LockClosedRegular,
  BookRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { veracityColors } from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import {
  hasGlobalAdmin,
  hasModuleAccess,
} from '@shared/auth';

type ModuleTile = {
  id: string;
  rolePrefix: string;
  name: string;
  description: string;
  path: string;
  icon: FluentIcon;
  status: 'live' | 'soon';
  accent: string;
};

const MODULES: ModuleTile[] = [
  {
    id: 'ops',
    rolePrefix: 'Ops',
    name: 'Operations',
    description: 'Group enrollment, plan year management, renewals',
    path: '/ops',
    icon: PeopleRegular,
    status: 'live',
    accent: veracityColors.veracityOrange,
  },
  {
    id: 'catalog',
    rolePrefix: 'Catalog',
    name: 'Catalog',
    description: 'Solutions, services, templates, managed lists',
    path: '/catalog',
    icon: BookTemplateRegular,
    status: 'live',
    accent: veracityColors.duskBlue,
  },
  {
    id: 'sales',
    rolePrefix: 'Sales',
    name: 'Sales',
    description: 'Pipeline tracking, broker scorecards, analytics',
    path: '/sales',
    icon: ChartMultipleRegular,
    status: 'live',
    accent: veracityColors.midnightCharcoal,
  },
  {
    id: 'analytics',
    rolePrefix: 'Analytics',
    name: 'Analytics',
    description: 'Cross-module reporting and insights',
    path: '#',
    icon: DataPieRegular,
    status: 'soon',
    accent: veracityColors.duskBlue,
  },
  {
    id: 'finance',
    rolePrefix: 'Finance',
    name: 'Finance',
    description: 'Billing, reconciliation, financial reporting',
    path: '#',
    icon: MoneyRegular,
    status: 'soon',
    accent: veracityColors.duskBlue,
  },
  {
    id: 'implementation',
    rolePrefix: 'Implementation',
    name: 'Implementation',
    description: 'Project tracking, section library, task manifests',
    path: '#',
    icon: WrenchScrewdriverRegular,
    status: 'soon',
    accent: veracityColors.duskBlue,
  },
];

/**
 * Returns a per-module access probe based on the user's token roles.
 * See packages/shared/src/auth/roles.ts for the canonical catalog.
 *
 * No fallback: a user with zero Vera roles sees the launcher's empty
 * state ("ask a Vera admin to assign you a role"), not every tile.
 * The empty state has a clear next action so the user isn't stuck.
 */
function useModuleAccess() {
  const { roles } = useAuth();

  return (rolePrefix: string) => ({
    hasAccess: hasModuleAccess(roles, rolePrefix) || hasGlobalAdmin(roles),
  });
}

const useStyles = makeStyles({
  page: {
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  hero: {
    marginBottom: tokens.spacingVerticalXXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  heroTitle: {
    color: veracityColors.midnightCharcoal,
  },
  heroAccent: {
    color: veracityColors.veracityOrange,
  },
  heroSubtitle: {
    color: veracityColors.duskBlue,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  tile: {
    position: 'relative',
    padding: tokens.spacingVerticalL,
    minHeight: '160px',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow8,
    },
  },
  tileDisabled: {
    position: 'relative',
    padding: tokens.spacingVerticalL,
    minHeight: '160px',
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  tileLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
  },
  tileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconGlyph: {
    fontSize: '22px',
    color: '#FFFFFF',
  },
  tileName: {
    color: veracityColors.midnightCharcoal,
  },
  tileDescription: {
    color: veracityColors.duskBlue,
  },
  cornerBadge: {
    position: 'absolute',
    top: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
  },
  emptyState: {
    gridColumn: '1 / -1',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

export function LauncherPage() {
  const styles = useStyles();
  const { user, roles } = useAuth();
  const accessFor = useModuleAccess();
  const firstName = user?.name?.split(' ')[0] ?? '';
  const isGlobalAdmin = hasGlobalAdmin(roles);

  const visibleModules = MODULES.map((m) => {
    if (m.status === 'soon') return { module: m, access: { hasAccess: false } };
    return { module: m, access: accessFor(m.rolePrefix) };
  }).filter(({ module, access }) => module.status === 'soon' || access.hasAccess);

  const hasLiveAccess = visibleModules.some(
    ({ module, access }) => module.status === 'live' && access.hasAccess
  );

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <Title1 className={styles.heroTitle}>
          Welcome Back{firstName ? `, ${firstName}` : ''}
        </Title1>
        <Body1 className={styles.heroSubtitle}>
          Choose a module to get started.
        </Body1>
      </div>

      <div className={styles.grid}>
        {!hasLiveAccess && !isGlobalAdmin && (
          <div className={styles.emptyState}>
            <Body1>
              You don't currently have access to any Vera modules. Ask a Vera
              admin to assign you the appropriate role.
            </Body1>
          </div>
        )}

        {visibleModules.map(({ module: m, access }) => {
          const Icon = m.icon;
          const isDisabled = m.status === 'soon';

          const inner = (
            <Card className={isDisabled ? styles.tileDisabled : styles.tile}>
              {m.status === 'soon' && (
                <Badge
                  appearance="outline"
                  color="informative"
                  className={styles.cornerBadge}
                >
                  Soon
                </Badge>
              )}
              <div className={styles.tileHeader}>
                <div
                  className={styles.iconCircle}
                  style={{ backgroundColor: m.accent }}
                >
                  <Icon className={styles.iconGlyph} />
                </div>
                <Title3 className={styles.tileName}>{m.name}</Title3>
              </div>
              <Body1 className={styles.tileDescription}>{m.description}</Body1>
            </Card>
          );

          if (isDisabled) {
            return <div key={m.id}>{inner}</div>;
          }

          return (
            <Link key={m.id} to={m.path} className={styles.tileLink}>
              {inner}
            </Link>
          );
        })}

        {isGlobalAdmin && (
          <Link to="/admin" className={styles.tileLink}>
            <Card className={styles.tile}>
              <Badge
                appearance="tint"
                color="brand"
                icon={<LockClosedRegular />}
                className={styles.cornerBadge}
              >
                Admin
              </Badge>
              <div className={styles.tileHeader}>
                <div
                  className={styles.iconCircle}
                  style={{ backgroundColor: veracityColors.midnightCharcoal }}
                >
                  <ShieldKeyholeRegular className={styles.iconGlyph} />
                </div>
                <Title3 className={styles.tileName}>Administration</Title3>
              </div>
              <Body1 className={styles.tileDescription}>
                Manage Vera role assignments and review user activity.
              </Body1>
            </Card>
          </Link>
        )}

        <Link to="/guide" className={styles.tileLink}>
          <Card className={styles.tile}>
            <div className={styles.tileHeader}>
              <div
                className={styles.iconCircle}
                style={{ backgroundColor: veracityColors.duskBlue }}
              >
                <BookRegular className={styles.iconGlyph} />
              </div>
              <Title3 className={styles.tileName}>Guide</Title3>
            </div>
            <Body1 className={styles.tileDescription}>
              In-app help and reference articles.
            </Body1>
          </Card>
        </Link>
      </div>
    </div>
  );
}
